/*
 * Gigglz "Create Your Own Card" configurator (WP6).
 *
 * Two phases: (1) format, then (2) a focused editor with tabs
 * (Photo / Border / Text / Inside) and a persistent live preview.
 *
 * The live preview mirrors aws/shared/rendering/custom_front_renderer.py exactly:
 * the frame is built at the render-box pixel size (e.g. 1529x2161 for a 7x5
 * front) and CSS-scaled to fit, so the on-screen result matches the print.
 * Cover-fill maths, border fractions, safe margin and text-size fractions come
 * from #cyoc-config (single source of truth with the server). Landscape uses the
 * swapped render frame (box.h x box.w); the 90-deg production rotation is a
 * print-fit detail the customer never sees.
 *
 * On add to basket the recipe is saved via the app proxy (/apps/gigglz/
 * custom-designs) and only the returned design id is written to the line item.
 * The delivery wizard then adds delivery details and resolves the Prodigi SKU.
 */
(function () {
  'use strict';

  var root = document.getElementById('cyoc-root');
  if (!root) return;

  var CFG;
  try {
    CFG = JSON.parse(document.getElementById('cyoc-config').textContent);
  } catch (e) {
    console.error('CYOC: could not parse config', e);
    return;
  }

  var $ = function (id) { return document.getElementById(id); };

  // Send List mode (?sl=1): the design is made for a card list, not the basket.
  // Single 7x5 only; the inside message is written on the list page, and the
  // finished design is handed back to the list page through localStorage
  // (same origin, works across tabs) instead of being added to the basket.
  var SL_RETURN_KEY = 'gigglz_sl_cyoc_return';
  var LIST_MODE = false;
  var LIST_RETURN = '';
  try {
    var _q = new URLSearchParams(window.location.search);
    LIST_MODE = _q.get('sl') === '1';
    var _r = String(_q.get('return') || '');
    if (/^\/pages\/[a-z0-9-]+$/.test(_r)) LIST_RETURN = _r; // same-site list pages only
  } catch (e) {}
  var el = {
    stage: $('cyoc-stage'),
    root: $('cyoc-root'),
    layout: document.querySelector('.cyoc__layout'),
    nav: document.querySelector('.cyoc__nav'),
    scrollcue: $('cyoc-scrollcue'),
    frameHolder: $('cyoc-frame-holder'),
    frame: $('cyoc-frame'),
    photo: $('cyoc-photo'),
    frontText: $('cyoc-front-text'),
    frontTextInner: $('cyoc-front-text-inner'),
    placeholder: $('cyoc-placeholder'),
    placeholderText: $('cyoc-placeholder-text'),
    placeholderProgress: $('cyoc-placeholder-progress'),
    placeholderBar: $('cyoc-placeholder-bar'),
    placeholderMsg: $('cyoc-placeholder-msg'),
    dpi: $('cyoc-dpi'),
    // phases + nav
    phaseFormat: $('cyoc-phase-format'),
    phaseEditor: $('cyoc-phase-editor'),
    navFormat: $('cyoc-nav-format'),
    navEditor: $('cyoc-nav-editor'),
    start: $('cyoc-start'),
    backBtn: $('cyoc-back-btn'),
    add: $('cyoc-add'),
    addError: $('cyoc-add-error'),
    editorSummary: $('cyoc-editor-summary'),
    tabs: $('cyoc-tabs'),
    // format
    kind: $('cyoc-kind'),
    size: $('cyoc-size'),
    sizeField: $('cyoc-size-field'),
    packInfo: $('cyoc-pack-info'),
    orientation: $('cyoc-orientation'),
    orientationEditor: $('cyoc-orientation-editor'),
    qtyLabel: $('cyoc-qty-label'),
    qty: $('cyoc-qty'),
    qtyMinus: $('cyoc-qty-minus'),
    qtyPlus: $('cyoc-qty-plus'),
    // photo tab
    photoUpload: $('cyoc-photo-upload'),
    photoControls: $('cyoc-photo-controls'),
    dropzone: $('cyoc-dropzone'),
    file: $('cyoc-file'),
    uploadStatus: $('cyoc-upload-status'),
    progressBar: $('cyoc-progress-bar'),
    uploadMsg: $('cyoc-upload-msg'),
    uploadError: $('cyoc-upload-error'),
    zoom: $('cyoc-zoom'),
    rotate: $('cyoc-rotate'),
    resetPos: $('cyoc-reset-pos'),
    replace: $('cyoc-replace'),
    // border
    borderWidth: $('cyoc-border-width'),
    borderColour: $('cyoc-border-colour'),
    // text
    frontTextInput: $('cyoc-front-text-input'),
    frontTextCount: $('cyoc-front-text-count'),
    frontFont: $('cyoc-front-font'),
    frontSize: $('cyoc-front-size'),
    frontPosition: $('cyoc-front-position'),
    frontColour: $('cyoc-front-colour'),
    // inside
    insidePackNote: $('cyoc-inside-pack-note'),
    insideCount: $('cyoc-inside-count'),
    insideLines: $('cyoc-inside-lines'),
    insideOver: $('cyoc-inside-over'),
    insideFont: $('cyoc-inside-font'),
    insideSize: $('cyoc-inside-size'),
    // preview faces + thumbnails
    inside: $('cyoc-inside'),
    insideText: $('cyoc-inside-text'),
    back: $('cyoc-back'),
    backImg: $('cyoc-back-img'),
    thumbs: $('cyoc-thumbs'),
    thumbFront: $('cyoc-thumb-front'),
    thumbMsg: $('cyoc-thumb-msg'),
    thumbInsideFront: $('cyoc-thumb-inside-front'),
    thumbInsideFrontLabel: $('cyoc-thumb-inside-front-label'),
    thumbInsideBackLabel: $('cyoc-thumb-inside-back-label'),
    pageSwitch: $('cyoc-pageswitch')
  };

  // ---- State --------------------------------------------------------------
  var state = {
    phase: 'format',           // format | editor
    tab: 'photo',
    face: 'front',             // front | inside-front | inside-back | back
    kind: 'single',            // single | pack
    size: '7x5',               // 7x5 | a4
    orientation: 'portrait',   // portrait | landscape
    quantity: 1,
    uploadedAssetKey: '',
    sourceWidth: 0,
    sourceHeight: 0,
    workingUrl: '',
    workingW: 0,
    workingH: 0,
    rotation: 0,
    zoom: 1,
    cropX: 0.5,
    cropY: 0.5,
    heicPreviewUnavailable: false,
    borderWidth: 'none',
    borderColour: 'white',
    frontText: '',
    frontFont: 'font-classic',
    frontColour: 'black',
    frontSize: 'medium',
    frontX: 0.5,               // normalised centre of the front-text block
    frontY: 1,                 // 1 => clamps to the bottom of the safe area
    frontPreset: 'bottom',     // top | centre | bottom | custom (for the preset buttons)
    insideMessage: '',        // inside-right (inside-back) page
    insideMessageLeft: '',    // inside-left (inside-front) page
    insideFont: 'font-classic',
    insideSize: 'Medium',
    insideTooLong: false,
    insideTooLongLeft: false,
    designFullId: ''
  };

  // The two inside pages read as left/right on a portrait card and top/bottom on
  // a landscape one (the card is the same folded sheet, viewed rotated). If a
  // landscape test print shows these two swapped, swap the landscape strings
  // here only; nothing else depends on the wording.
  var INSIDE_LABELS = {
    portrait: { 'inside-front': 'Left', 'inside-back': 'Right' },
    landscape: { 'inside-front': 'Top', 'inside-back': 'Bottom' }
  };
  function insideFaceLabel(face) {
    var set = INSIDE_LABELS[state.orientation] || INSIDE_LABELS.portrait;
    return set[face] || 'Inside';
  }
  function isInsideFace(face) { return face === 'inside-front' || face === 'inside-back'; }
  function activeInsideField() {
    return state.face === 'inside-front' ? 'insideMessageLeft' : 'insideMessage';
  }
  function getActiveInsideMessage() { return state[activeInsideField()] || ''; }

  var sourceImage = null;
  var insideMirror = null;   // hidden element for measuring inside-message lines

  // ---- Helpers ------------------------------------------------------------
  function money(v) {
    var n = Number(v);
    if (!isFinite(n)) return '';
    return '£' + n.toFixed(2);
  }
  function clamp(n, lo, hi) { return Math.min(hi, Math.max(lo, n)); }
  function makeUid() { return Date.now() + '-' + Math.random().toString(16).slice(2); }

  function activeVariantKey() { return state.kind === 'pack' ? 'pack' : state.size; }
  function activeVariant() { return CFG.variants[activeVariantKey()]; }
  function frontBox() { return CFG.frontBox[state.size]; }

  function renderDims() {
    var b = frontBox();
    if (state.orientation === 'landscape') return { w: b.h, h: b.w };
    return { w: b.w, h: b.h };
  }

  // Selectable groups: any element carrying `attr` inside `group`.
  function wireToggle(group, attr, onPick) {
    if (!group) return;
    group.addEventListener('click', function (ev) {
      var btn = ev.target.closest('[' + attr + ']');
      if (!btn || btn.disabled || !group.contains(btn)) return;
      Array.prototype.forEach.call(group.querySelectorAll('[' + attr + ']'), function (b) {
        var on = b === btn;
        b.classList.toggle('is-selected', on);
        b.setAttribute('aria-checked', on ? 'true' : 'false');
      });
      onPick(btn.getAttribute(attr));
    });
  }

  function buildSwatches(container, selectedSlug, onPick) {
    container.innerHTML = '';
    Object.keys(CFG.palette).forEach(function (slug) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'cyoc__swatch' + (slug === selectedSlug ? ' is-selected' : '');
      b.style.background = CFG.palette[slug];
      b.title = CFG.paletteLabels[slug] || slug;
      b.setAttribute('aria-label', CFG.paletteLabels[slug] || slug);
      b.addEventListener('click', function () {
        Array.prototype.forEach.call(container.children, function (c) { c.classList.remove('is-selected'); });
        b.classList.add('is-selected');
        onPick(slug);
      });
      container.appendChild(b);
    });
  }

  // Optical size multipliers so each font previews at a comparable visual size
  // in the picker (mirrors the personalisation page's per-font adjustments).
  var FONT_OPTICAL = {
    'font-scribble': 1.5,     // Reenie Beanie
    'font-love': 1.375,       // Dancing Script
    'font-friendly': 1.125,   // Patrick Hand (inside)
    'font-statement': 1.125,  // Paytone One (inside)
    'font-handwritten': 1.125,// Patrick Hand (front)
    'font-playful': 1.125,    // Paytone One (front)
    'font-bold': 0.95         // Archivo Black (front)
  };

  // A custom font picker that previews each option in its own typeface,
  // matching the personalisation page. Replaces a plain <select>, whose
  // option labels always render in the OS font. state.*Font stays the
  // single source of truth; onPick(slug) fires on selection.
  function buildFontPicker(container, fonts, selectedSlug, labelId, onPick) {
    container.innerHTML = '';
    container.classList.add('cyoc__font-picker');
    var open = false;

    var current = document.createElement('button');
    current.type = 'button';
    current.className = 'cyoc__font-current';
    current.id = container.id + '-current';
    current.setAttribute('aria-haspopup', 'listbox');
    current.setAttribute('aria-expanded', 'false');
    if (labelId) current.setAttribute('aria-labelledby', labelId + ' ' + current.id);
    // Font sizing goes on an inner span, not the button, so the per-font
    // optical size never inflates the button's em-based padding.
    var currentText = document.createElement('span');
    current.appendChild(currentText);

    var list = document.createElement('div');
    list.className = 'cyoc__font-list';
    list.setAttribute('role', 'listbox');
    list.hidden = true;

    function applyCurrent(slug) {
      var f = fonts[slug] || fonts[Object.keys(fonts)[0]];
      currentText.textContent = f.label;
      currentText.style.fontFamily = f.stack;
      currentText.style.fontSize = (FONT_OPTICAL[slug] || 1) + 'em';
    }
    function setOpen(v) {
      open = v;
      list.hidden = !v;
      current.setAttribute('aria-expanded', v ? 'true' : 'false');
      container.classList.toggle('is-open', v);
    }

    Object.keys(fonts).forEach(function (slug) {
      var opt = document.createElement('button');
      opt.type = 'button';
      opt.className = 'cyoc__font-option' + (slug === selectedSlug ? ' is-selected' : '');
      opt.setAttribute('role', 'option');
      opt.setAttribute('data-font', slug);
      opt.setAttribute('aria-selected', slug === selectedSlug ? 'true' : 'false');
      var otext = document.createElement('span');
      otext.textContent = fonts[slug].label;
      otext.style.fontFamily = fonts[slug].stack;
      otext.style.fontSize = (FONT_OPTICAL[slug] || 1) + 'em';
      opt.appendChild(otext);
      opt.addEventListener('click', function () {
        selectedSlug = slug;
        Array.prototype.forEach.call(list.children, function (c) {
          var on = c === opt;
          c.classList.toggle('is-selected', on);
          c.setAttribute('aria-selected', on ? 'true' : 'false');
        });
        applyCurrent(slug);
        setOpen(false);
        current.focus();
        onPick(slug);
      });
      list.appendChild(opt);
    });

    current.addEventListener('click', function (ev) { ev.stopPropagation(); setOpen(!open); });
    current.addEventListener('keydown', function (ev) {
      if (ev.key === 'Escape') { setOpen(false); }
      else if (!open && (ev.key === 'ArrowDown' || ev.key === 'Enter' || ev.key === ' ')) {
        ev.preventDefault(); setOpen(true);
        var first = list.querySelector('.cyoc__font-option'); if (first) first.focus();
      }
    });
    list.addEventListener('keydown', function (ev) {
      if (ev.key === 'Escape') { setOpen(false); current.focus(); }
    });
    document.addEventListener('click', function (ev) {
      if (open && !container.contains(ev.target)) setOpen(false);
    });

    applyCurrent(selectedSlug);
    container.appendChild(current);
    container.appendChild(list);
  }

  function fillSizeSelect(select, sizes, selected) {
    select.innerHTML = '';
    sizes.forEach(function (s) {
      var o = document.createElement('option');
      o.value = s;
      o.textContent = s;
      if (s === selected) o.selected = true;
      select.appendChild(o);
    });
  }

  // ---- Preview engine (mirrors custom_front_renderer.py) ------------------
  // Cache the viewport height used for scaling so the preview does NOT resize
  // when the mobile address bar hides/shows on scroll (which changes
  // window.innerHeight and fires resize). Refreshed only on a real width or
  // orientation change and on entering the editor.
  var previewScaleH = (typeof window !== 'undefined') ? window.innerHeight : 700;
  function refreshPreviewScaleH() { previewScaleH = window.innerHeight; }
  function fitScale(renderW, renderH) {
    // Cap the preview by the stage's REAL horizontal padding (80px desktop /
    // 36px mobile) rather than a hard-coded 32px. The fudge under-counted the
    // padding, so in landscape (where width is the binding constraint) the
    // holder was sized wider than the stage content box: it overflowed and was
    // flex-shrunk, while the absolutely-positioned frame kept its full width.
    // That left the empty-state "Tap to add your photo" placeholder (sized to
    // the shrunk holder) off-centre against the visible card. Portrait is
    // height-bound, so its scale is unchanged.
    var cs = window.getComputedStyle(el.stage);
    var padX = (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0);
    var availW = (el.stage.clientWidth || 320) - padX;
    var maxH = Math.min((previewScaleH || window.innerHeight) * 0.6, 620);
    return Math.max(0.02, Math.min(availW / renderW, maxH / renderH));
  }

  // Mobile scroll cue: show a bouncing chevron above the sticky footer when
  // there is configurator content below the fold, hide it once the bottom is
  // reached. Desktop (>=860px) never shows it.
  function updateScrollCue() {
    if (!el.scrollcue) return;
    if (window.innerWidth >= 860) { el.scrollcue.hidden = true; return; }
    var navH = el.nav ? el.nav.offsetHeight : 84;
    var bottom = el.layout ? el.layout.getBoundingClientRect().bottom : 0;
    var more = bottom > (window.innerHeight - navH - 6);
    el.scrollcue.hidden = !more;
  }

  function renderPreview() {
    var dims = renderDims();
    var renderW = dims.w, renderH = dims.h;
    var shortSide = Math.min(renderW, renderH);
    var s = fitScale(renderW, renderH);

    el.frame.style.width = renderW + 'px';
    el.frame.style.height = renderH + 'px';
    el.frame.style.transform = 'scale(' + s + ')';
    el.frame.style.transformOrigin = 'top left';
    el.frameHolder.style.width = Math.round(renderW * s) + 'px';
    el.frameHolder.style.height = Math.round(renderH * s) + 'px';

    // Reset all layers; each face turns on what it needs.
    el.frontText.hidden = true;
    el.inside.hidden = true;
    el.back.hidden = true;
    el.placeholder.hidden = true;
    el.dpi.hidden = true;
    el.photo.style.display = 'none';
    el.frame.classList.remove('is-empty', 'is-fold-left', 'is-fold-right', 'is-fold-top', 'is-fold-bottom');

    if (state.face === 'front') {
      renderFront(renderW, renderH, shortSide);
    } else if (isInsideFace(state.face)) {
      renderInside(renderW, renderH);
      el.frame.classList.add(insideFoldClass(state.face));
    } else if (state.face === 'back') {
      el.frame.style.background = '#fff';
      if (el.backImg && CFG.backImages) el.backImg.src = state.orientation === 'landscape' ? (CFG.backImages.landscape || CFG.backImages.portrait) : CFG.backImages.portrait;
      el.back.hidden = false;
    } else {
      el.frame.style.background = '#fff';
    }

    updateThumbs();
  }

  // Which edge of the inside page preview is the spine, so a soft fold shadow
  // makes it read as one half of an open card. Portrait: the right page folds
  // on its left edge and vice versa. Landscape: the bottom page folds on its top
  // edge and vice versa (matches INSIDE_LABELS).
  function insideFoldClass(face) {
    if (state.orientation === 'landscape') return face === 'inside-back' ? 'is-fold-top' : 'is-fold-bottom';
    return face === 'inside-back' ? 'is-fold-left' : 'is-fold-right';
  }

  // Small open-card pictogram with the selected half filled.
  function pageIcon(face) {
    var on = 'fill="currentColor"';
    var off = 'fill="none" stroke="currentColor" stroke-width="1.5" opacity=".55"';
    var first = face === 'inside-front' ? on : off;
    var second = face === 'inside-back' ? on : off;
    if (state.orientation === 'landscape') {
      return '<svg width="22" height="26" viewBox="0 0 22 26" focusable="false">' +
        '<rect x="1" y="1" width="20" height="11" rx="1.5" ' + first + '/>' +
        '<rect x="1" y="14" width="20" height="11" rx="1.5" ' + second + '/></svg>';
    }
    return '<svg width="28" height="20" viewBox="0 0 28 20" focusable="false">' +
      '<rect x="1" y="1" width="12" height="18" rx="1.5" ' + first + '/>' +
      '<rect x="15" y="1" width="12" height="18" rx="1.5" ' + second + '/></svg>';
  }

  // Inside-page switcher above the card: visible only on the Inside tab. Shows
  // which page is being edited and whether each page has a message yet.
  function updatePageSwitch() {
    if (!el.pageSwitch) return;
    var show = state.phase === 'editor' && state.tab === 'inside' && isInsideFace(state.face);
    el.pageSwitch.hidden = !show;
    if (!show) return;
    Array.prototype.forEach.call(el.pageSwitch.querySelectorAll('.cyoc__ps-opt'), function (b) {
      var face = b.getAttribute('data-page');
      var on = face === state.face;
      b.classList.toggle('is-selected', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
      var word = b.querySelector('.cyoc__ps-word');
      if (word) word.textContent = insideFaceLabel(face);
      var text = face === 'inside-front' ? state.insideMessageLeft : state.insideMessage;
      var st = b.querySelector('.cyoc__ps-status');
      if (st) st.textContent = String(text || '').trim() ? '✓ Added' : 'Empty';
      var ic = b.querySelector('.cyoc__ps-icon');
      if (ic) ic.innerHTML = pageIcon(face);
    });
  }

  function renderFront(renderW, renderH, shortSide) {
    el.photo.style.display = '';
    var borderFrac = CFG.borderFraction[state.borderWidth] || 0;
    var borderPx = Math.round(borderFrac * shortSide);
    el.frame.style.background = borderPx > 0 ? (CFG.palette[state.borderColour] || '#FFFFFF') : '#FFFFFF';
    el.photo.style.top = borderPx + 'px';
    el.photo.style.left = borderPx + 'px';
    el.photo.style.right = borderPx + 'px';
    el.photo.style.bottom = borderPx + 'px';

    var hasPhoto = !!(state.workingUrl && state.workingW && state.workingH);
    el.frame.classList.toggle('is-empty', !state.uploadedAssetKey);
    if (hasPhoto) {
      var innerW = Math.max(1, renderW - 2 * borderPx);
      var innerH = Math.max(1, renderH - 2 * borderPx);
      var coverScale = Math.max(innerW / state.workingW, innerH / state.workingH);
      el.photo.style.backgroundImage = 'url("' + state.workingUrl + '")';
      el.photo.style.backgroundSize = (state.workingW * coverScale * state.zoom).toFixed(3) + 'px ' + (state.workingH * coverScale * state.zoom).toFixed(3) + 'px';
      el.photo.style.backgroundPosition = (state.cropX * 100).toFixed(4) + '% ' + (state.cropY * 100).toFixed(4) + '%';
      updateDpi(coverScale);
      el.dpi.hidden = false;
    } else {
      el.photo.style.backgroundImage = '';
      el.placeholder.hidden = false;
      el.placeholderText.textContent = (state.uploadedAssetKey && state.heicPreviewUnavailable)
        ? "We can't preview this file type here, but your photo will print fine."
        : 'Tap to add your photo';
    }

    if (state.frontText.trim() && hasPhoto) {
      var fontPx = Math.round((CFG.frontTextSizeFraction[state.frontSize] || 0.085) * shortSide);
      var safePx = Math.round(CFG.safeMarginFraction * shortSide);
      el.frontText.hidden = false;
      var f = CFG.frontFonts[state.frontFont] || CFG.frontFonts['font-classic'];
      var inner = el.frontTextInner;
      inner.textContent = state.frontText;
      inner.style.position = 'absolute';
      inner.style.fontFamily = f.stack;
      inner.style.fontSize = fontPx + 'px';
      inner.style.lineHeight = '1.15';
      inner.style.color = CFG.palette[state.frontColour] || '#141414';
      inner.style.maxWidth = Math.max(1, renderW - 2 * safePx) + 'px';
      positionFrontText(renderW, renderH, safePx);
      // Show the "draggable" affordance only on the Text tab.
      el.frontText.classList.toggle('is-editable', state.tab === 'text');
    } else {
      el.frontText.hidden = true;
      el.frontText.classList.remove('is-editable');
    }
  }

  // Position the front-text block at the (clamped) front_x/front_y centre, so
  // it can sit anywhere the customer drags it while staying inside the print
  // safe margin. custom_front_renderer.py positions it identically for print.
  function positionFrontText(renderW, renderH, safePx) {
    var inner = el.frontTextInner;
    var bw = inner.offsetWidth, bh = inner.offsetHeight; // render-px layout size (transform-independent)
    var halfWn = (bw / 2) / renderW, halfHn = (bh / 2) / renderH;
    var mXn = safePx / renderW, mYn = safePx / renderH;
    var minX = mXn + halfWn, maxX = 1 - mXn - halfWn;
    var minY = mYn + halfHn, maxY = 1 - mYn - halfHn;
    var cx = (minX <= maxX) ? clamp(state.frontX, minX, maxX) : 0.5;
    var cy = (minY <= maxY) ? clamp(state.frontY, minY, maxY) : 0.5;
    state.frontX = cx; state.frontY = cy; // store the clamped (printed) position
    inner.style.left = (cx * 100).toFixed(4) + '%';
    inner.style.top = (cy * 100).toFixed(4) + '%';
    inner.style.transform = 'translate(-50%, -50%)';
  }

  // Quick presets set a raw target (top/centre/bottom); renderFront clamps it
  // into the safe area, so they behave like the old top/centre/bottom options.
  function applyFrontPreset(pos) {
    if (pos !== 'top' && pos !== 'centre' && pos !== 'bottom') return;
    state.frontPreset = pos;
    state.frontX = 0.5;
    state.frontY = pos === 'top' ? 0 : (pos === 'centre' ? 0.5 : 1);
    syncFrontPresetButtons();
    renderPreview();
  }
  function syncFrontPresetButtons() {
    if (!el.frontPosition) return;
    Array.prototype.forEach.call(el.frontPosition.querySelectorAll('[data-position]'), function (b) {
      var on = b.getAttribute('data-position') === state.frontPreset;
      b.classList.toggle('is-selected', on);
      b.setAttribute('aria-checked', on ? 'true' : 'false');
    });
  }
  // Drag the front-text block anywhere within the safe area.
  function wireFrontTextDrag() {
    var inner = el.frontTextInner;
    if (!inner) return;
    var dragging = false, startX = 0, startY = 0, startFX = 0, startFY = 0, bounds = null;
    inner.addEventListener('pointerdown', function (e) {
      if (state.tab !== 'text' || !state.frontText.trim()) return;
      e.stopPropagation(); // don't also start a photo drag on the frame
      e.preventDefault();
      try { inner.setPointerCapture(e.pointerId); } catch (ex) {}
      dragging = true;
      startX = e.clientX; startY = e.clientY;
      startFX = state.frontX; startFY = state.frontY;
      var dims = renderDims();
      var rect = el.frame.getBoundingClientRect();
      var safePx = Math.round(CFG.safeMarginFraction * Math.min(dims.w, dims.h));
      var halfWn = (inner.offsetWidth / 2) / dims.w, halfHn = (inner.offsetHeight / 2) / dims.h;
      var mXn = safePx / dims.w, mYn = safePx / dims.h;
      bounds = {
        minX: mXn + halfWn, maxX: 1 - mXn - halfWn,
        minY: mYn + halfHn, maxY: 1 - mYn - halfHn,
        rw: rect.width, rh: rect.height
      };
      inner.classList.add('is-dragging');
      state.frontPreset = 'custom';
      syncFrontPresetButtons();
    });
    inner.addEventListener('pointermove', function (e) {
      if (!dragging || !bounds) return;
      var dxn = (e.clientX - startX) / (bounds.rw || 1);
      var dyn = (e.clientY - startY) / (bounds.rh || 1);
      var cx = bounds.minX <= bounds.maxX ? clamp(startFX + dxn, bounds.minX, bounds.maxX) : 0.5;
      var cy = bounds.minY <= bounds.maxY ? clamp(startFY + dyn, bounds.minY, bounds.maxY) : 0.5;
      state.frontX = cx; state.frontY = cy;
      inner.style.left = (cx * 100).toFixed(4) + '%';
      inner.style.top = (cy * 100).toFixed(4) + '%';
    });
    function endDrag() { dragging = false; bounds = null; inner.classList.remove('is-dragging'); }
    inner.addEventListener('pointerup', endDrag);
    inner.addEventListener('pointercancel', endDrag);
  }

  // Inside-message font px, matching the print (typography.py). Scribble is 1.2x.
  function insidePx() {
    var px = (CFG.insideSizePx && CFG.insideSizePx[state.insideSize]) || 74;
    if (state.insideFont === 'font-scribble') px = Math.round(px * 1.2);
    return px;
  }

  // ---- Inside message: edit directly on the preview (contenteditable) ----
  // Mirrors the personalisation page. #cyoc-inside-text is the sole editor for
  // the inside message (no separate textarea); state.insideMessage stays the
  // single source of truth for the recipe.
  function normaliseText(raw) {
    return String(raw || '').replace(/\u00A0/g, ' ').replace(/\u200B/g, '').replace(/\r/g, '');
  }
  function stripOuterBlankLines(text) {
    return String(text || '').replace(/^(?:[ \t]*\n)+/, '').replace(/(?:\n[ \t]*)+$/, '');
  }
  function editorDomToText(root) {
    var parts = [];
    function walk(node) {
      if (!node) return;
      if (node.nodeType === 3) { parts.push(node.nodeValue || ''); return; }
      if (node.nodeType !== 1) return;
      if (node.tagName === 'BR') { parts.push('\n'); return; }
      var isBlock = node.tagName === 'DIV' || node.tagName === 'P' || node.tagName === 'LI';
      Array.prototype.forEach.call(node.childNodes || [], walk);
      if (isBlock && parts.length) {
        var last = parts[parts.length - 1] || '';
        if (last.charAt(last.length - 1) !== '\n') parts.push('\n');
      }
    }
    Array.prototype.forEach.call(root.childNodes || [], walk);
    return parts.join('');
  }
  function getInsideEditorText() {
    var txt = stripOuterBlankLines(normaliseText(editorDomToText(el.insideText)));
    return txt.trim() === '' ? '' : txt;
  }
  function markInsideEmpty(isEmpty) {
    el.insideText.setAttribute('data-empty', isEmpty ? 'true' : 'false');
  }
  function setInsideEditorText(text) {
    var t = stripOuterBlankLines(normaliseText(text));
    if (t.trim().length === 0) { el.insideText.innerHTML = '<br>'; markInsideEmpty(true); }
    else { el.insideText.textContent = t; markInsideEmpty(false); }
  }
  function placeCaretAtEnd(node) {
    if (!node) return;
    node.focus();
    var sel = window.getSelection();
    if (!sel) return;
    var range = document.createRange();
    range.selectNodeContents(node);
    range.collapse(false);
    sel.removeAllRanges();
    sel.addRange(range);
  }
  function insertLineBreak() {
    try {
      if (document.queryCommandSupported && document.queryCommandSupported('insertLineBreak')) {
        document.execCommand('insertLineBreak');
        return;
      }
    } catch (e) {}
    document.execCommand('insertHTML', false, '<br>');
  }
  // Typing on the preview -> state + textarea + counters (no full re-render,
  // so the caret is never disturbed).
  function onInsideEditorInput() {
    var text = getInsideEditorText();
    if (text.length > CFG.maxInsideMessage) {
      text = text.slice(0, CFG.maxInsideMessage);
      setInsideEditorText(text);
      placeCaretAtEnd(el.insideText);
    }
    state[activeInsideField()] = text;
    el.insideCount.textContent = String(text.length);
    markInsideEmpty(text.length === 0);
    measureInside();
    updateThumbs();
    updateAddState();
  }
  function insideEditorKeydown(ev) {
    if (ev.key === 'Enter') { ev.preventDefault(); insertLineBreak(); onInsideEditorInput(); }
  }
  function onInsideEditorPaste(ev) {
    ev.preventDefault();
    var cd = ev.clipboardData || window.clipboardData;
    document.execCommand('insertText', false, cd ? cd.getData('text') : '');
  }

  // Renders the inside face exactly like the print panel: message centred both
  // ways, line breaks preserved (To.../message/...from), pad 96x92, lh 1.25.
  function renderInside(renderW, renderH) {
    el.frame.style.background = '#fff';
    el.inside.hidden = false;
    var box = CFG.frontBox[state.size];
    var safe = (CFG.insideSafe && CFG.insideSafe[state.size]) || { x: 0.1 * box.w, y: 0.12 * box.h, w: 0.78 * box.w, h: 0.74 * box.h };
    // The inside-right box sits toward the fold (its left); the inside-left box
    // mirrors it, sitting toward the fold on its right. Matches the print's
    // message_safe / message_safe_front geometry.
    if (state.face === 'inside-front') {
      safe = { x: box.w - safe.x - safe.w, y: safe.y, w: safe.w, h: safe.h };
    }
    var t = el.insideText;
    t.setAttribute('data-placeholder', 'Type your ' + insideFaceLabel(state.face).toLowerCase() + ' page message…');
    t.style.left = (safe.x / box.w * renderW) + 'px';
    t.style.top = (safe.y / box.h * renderH) + 'px';
    t.style.width = (safe.w / box.w * renderW) + 'px';
    t.style.height = (safe.h / box.h * renderH) + 'px';
    t.style.boxSizing = 'border-box';
    // Column flex (like the personalisation editor): centres the message block
    // vertically while a lone <br> on an empty line still shows a caret. A row
    // flex hides the empty-state caret until the first character is typed.
    t.style.display = 'flex';
    t.style.flexDirection = 'column';
    t.style.alignItems = 'stretch';
    t.style.justifyContent = 'center';
    t.style.textAlign = 'center';
    t.style.whiteSpace = 'pre-wrap';
    t.style.overflowWrap = 'anywhere';
    t.style.wordBreak = 'break-word';
    t.style.padding = '92px 96px';
    t.style.lineHeight = '1.25';
    var f = CFG.insideFonts[state.insideFont] || CFG.insideFonts['font-classic'];
    t.style.fontFamily = f.stack;
    t.style.fontSize = insidePx() + 'px';
    t.style.color = '#333';
    // The message is edited directly on the preview; don't overwrite it while
    // the customer is typing there (it would clobber their caret). Sync from
    // state only when the preview editor isn't focused. Reads whichever inside
    // page is active.
    if (document.activeElement !== t) setInsideEditorText(getActiveInsideMessage());
    measureInside();
  }

  // Line-limit measurement, mirroring the personalisation page.
  function measureInside() {
    var safe = (CFG.insideSafe && CFG.insideSafe[state.size]) || { w: 1176, h: 1629 };
    var innerWidth = safe.w - 96 * 2;
    var innerHeight = safe.h - 92 * 2;
    var px = insidePx();
    var lineHeightPx = px * 1.25;
    var f = CFG.insideFonts[state.insideFont] || CFG.insideFonts['font-classic'];

    if (!insideMirror) {
      insideMirror = document.createElement('div');
      var st = insideMirror.style;
      st.position = 'absolute'; st.top = '-99999px'; st.left = '-99999px';
      st.visibility = 'hidden'; st.pointerEvents = 'none';
      st.whiteSpace = 'pre-wrap'; st.overflowWrap = 'anywhere'; st.wordBreak = 'break-word';
      st.boxSizing = 'content-box'; st.padding = '0'; st.border = '0'; st.textAlign = 'center';
      document.body.appendChild(insideMirror);
    }
    var m = insideMirror;
    m.style.width = innerWidth + 'px';
    m.style.fontFamily = f.stack;
    m.style.fontSize = px + 'px';
    m.style.lineHeight = '1.25';
    function measureText(raw) {
      m.textContent = raw || '';
      var usedHeight = raw ? m.getBoundingClientRect().height : 0;
      var usedLines = usedHeight === 0 ? 0 : Math.max(1, Math.round(usedHeight / lineHeightPx));
      return { usedLines: usedLines, tooLong: usedHeight > (innerHeight + 1) };
    }

    var maxLines = Math.max(1, Math.floor((innerHeight + 0.5) / lineHeightPx));
    // Keep BOTH page flags current, so a too-long page always blocks add-to-basket
    // even when the other page is the one on screen.
    state.insideTooLong = measureText(state.insideMessage).tooLong;
    state.insideTooLongLeft = measureText(state.insideMessageLeft).tooLong;

    // The line counter and warning reflect the page currently being edited.
    var active = measureText(getActiveInsideMessage());
    if (el.insideLines) {
      el.insideLines.hidden = active.usedLines === 0;
      el.insideLines.textContent = active.usedLines + ' / ' + maxLines + ' lines';
      el.insideLines.classList.toggle('is-over', active.tooLong);
    }
    if (el.insideOver) el.insideOver.hidden = !active.tooLong;
    return active.tooLong;
  }

  function updateThumbs() {
    Array.prototype.forEach.call(el.thumbs.querySelectorAll('.cyoc__thumb'), function (b) {
      b.classList.toggle('is-active', b.getAttribute('data-face') === state.face);
    });
    if (state.workingUrl && el.thumbFront) {
      el.thumbFront.style.backgroundImage = 'url("' + state.workingUrl + '")';
      el.thumbFront.innerHTML = '';
    }
    if (el.thumbMsg) {
      var msg = state.insideMessage.trim();
      el.thumbMsg.textContent = msg ? (msg.length > 6 ? msg.slice(0, 6) + '…' : msg) : 'Aa';
    }
    if (el.thumbInsideFront) {
      var msgL = state.insideMessageLeft.trim();
      el.thumbInsideFront.textContent = msgL ? (msgL.length > 6 ? msgL.slice(0, 6) + '…' : msgL) : 'Aa';
    }
    if (el.thumbInsideFrontLabel) el.thumbInsideFrontLabel.textContent = insideFaceLabel('inside-front');
    if (el.thumbInsideBackLabel) el.thumbInsideBackLabel.textContent = insideFaceLabel('inside-back');
    // Keep the page switcher's selection and "Empty / Added" status in step
    // (updateThumbs runs on every render and on every keystroke in the editor).
    updatePageSwitch();
  }

  function updateDpi(coverScale) {
    var factor = coverScale * state.zoom;
    var eff = Math.round(300 / factor);
    el.dpi.classList.remove('is-warn', 'is-bad');
    if (factor <= 1) {
      el.dpi.textContent = 'Print quality: great';
    } else if (eff >= 150) {
      el.dpi.textContent = 'Print quality: may look a little soft (' + eff + ' dpi)';
      el.dpi.classList.add('is-warn');
    } else {
      el.dpi.textContent = 'Print quality: too low (' + eff + ' dpi) — zoom out or use a bigger photo';
      el.dpi.classList.add('is-bad');
    }
  }

  function dpiTooLow() {
    if (!state.workingW || !state.workingH) return false;
    var dims = renderDims();
    var borderPx = Math.round((CFG.borderFraction[state.borderWidth] || 0) * Math.min(dims.w, dims.h));
    var innerW = Math.max(1, dims.w - 2 * borderPx);
    var innerH = Math.max(1, dims.h - 2 * borderPx);
    var coverScale = Math.max(innerW / state.workingW, innerH / state.workingH);
    return (300 / (coverScale * state.zoom)) < 150;
  }

  // ---- Working image (bakes user rotation, like Pillow rotate) ------------
  function rebuildWorkingImage() {
    if (!sourceImage) return;
    var w = sourceImage.naturalWidth, h = sourceImage.naturalHeight;
    var rot = ((state.rotation % 360) + 360) % 360;
    var canvas = document.createElement('canvas');
    var ctx = canvas.getContext('2d');
    if (rot === 90 || rot === 270) { canvas.width = h; canvas.height = w; }
    else { canvas.width = w; canvas.height = h; }
    ctx.save();
    if (rot === 90) { ctx.translate(h, 0); ctx.rotate(Math.PI / 2); }
    else if (rot === 180) { ctx.translate(w, h); ctx.rotate(Math.PI); }
    else if (rot === 270) { ctx.translate(0, w); ctx.rotate(3 * Math.PI / 2); }
    ctx.drawImage(sourceImage, 0, 0);
    ctx.restore();
    if (state.workingUrl && state.workingUrl.indexOf('blob:') === 0) {
      try { URL.revokeObjectURL(state.workingUrl); } catch (e) {}
    }
    try {
      state.workingUrl = canvas.toDataURL('image/jpeg', 0.92);
    } catch (e) {
      state.workingUrl = '';
    }
    state.workingW = canvas.width;
    state.workingH = canvas.height;
  }

  // ---- Upload -------------------------------------------------------------
  // A JS-driven "filling" progress bar: width-only, so it renders reliably and
  // doesn't depend on any keyframe/is-busy CSS (which wasn't showing). It eases
  // toward 90% while uploading, then snaps to 100% on completion.
  // Two bars are driven together: the editor photo-panel bar and one on the
  // format-screen "add photo" placeholder. Whichever is on screen (depending on
  // where the upload was started) shows; the other sits in a hidden phase and is
  // harmless. CSS forces display:block so Dawn's div:empty rule can't hide the
  // (empty) bar.
  function fillBar(bar) {
    if (!bar) return;
    bar.style.transition = 'none';
    bar.style.width = '0%';
    void bar.offsetWidth; // reflow so the fill animates from 0
    bar.style.transition = 'width 8s cubic-bezier(0.15, 0.85, 0.35, 1)';
    bar.style.width = '90%';
  }
  function completeBar(bar) {
    if (!bar) return;
    bar.style.transition = 'width 0.25s ease';
    bar.style.width = '100%';
  }
  function resetBar(bar) {
    if (!bar) return;
    bar.style.transition = 'none';
    bar.style.width = '0%';
  }
  function startUploadProgress() {
    fillBar(el.progressBar);
    fillBar(el.placeholderBar);
  }
  function finishUploadProgress() {
    completeBar(el.progressBar);
    completeBar(el.placeholderBar);
  }
  function showPlaceholderUploading(show) {
    if (el.placeholderProgress) el.placeholderProgress.hidden = !show;
    if (el.placeholderText) el.placeholderText.hidden = show;
  }
  function resetUploadUi() {
    el.uploadStatus.hidden = true;
    el.uploadError.hidden = true;
    showPlaceholderUploading(false);
    resetBar(el.progressBar);
    resetBar(el.placeholderBar);
  }
  function showUploadError(msg) {
    el.uploadError.textContent = msg;
    el.uploadError.hidden = false;
    el.uploadStatus.hidden = true;
    showPlaceholderUploading(false);
  }
  function showPhotoControls(show) {
    el.photoUpload.hidden = show;
    el.photoControls.hidden = !show;
  }

  // iPhones shoot HEIC/HEIF, which most browsers cannot display and which our
  // server renders best as JPEG. Convert to JPEG in the browser (decoder is
  // lazy-loaded only when a HEIC is actually chosen) and use the JPEG for both
  // the preview and the upload, so the customer sees their photo and everything
  // downstream gets a format it can read.
  // iPhones shoot HEIC/HEIF. Most browsers except Safari can't display it, and
  // large iPhone HEICs are grid-tiled, which older decoders (heic2any) can't
  // reassemble. We decode with a modern libheif build, trying the browser's own
  // decoder first (Safari) so it stays fast on iPhone/Mac Safari. The result
  // JPEG is used for both the preview and the upload, so everything downstream
  // gets a format it can read.
  var HEIC_LIB_URL = 'https://cdn.jsdelivr.net/npm/libheif-js@1.23.2/libheif/libheif.js';
  function isHeicFile(file) {
    return /image\/hei[cf]/i.test(file.type || '') || /\.(heic|heif)$/i.test(file.name || '');
  }
  function ensureHeicLib() {
    if (window.libheif) return Promise.resolve();
    if (window.__cyocHeicLoading) return window.__cyocHeicLoading;
    var primary = (CFG && CFG.heicLibUrl) ? CFG.heicLibUrl : HEIC_LIB_URL;
    function loadScript(src) {
      return new Promise(function (resolve, reject) {
        var sc = document.createElement('script');
        sc.src = src;
        sc.onload = function () { resolve(); };
        sc.onerror = function () { reject(new Error('Could not load the photo converter.')); };
        document.head.appendChild(sc);
      });
    }
    window.__cyocHeicLoading = loadScript(primary).catch(function () {
      if (primary === HEIC_LIB_URL) throw new Error('Could not load the photo converter.');
      return loadScript(HEIC_LIB_URL);
    });
    return window.__cyocHeicLoading;
  }
  function setUploadMessage(text) {
    if (el.uploadMsg) el.uploadMsg.textContent = text;
    if (el.placeholderMsg) el.placeholderMsg.textContent = text;
  }
  function canvasToJpegFile(canvas, name) {
    return new Promise(function (resolve, reject) {
      canvas.toBlob(function (blob) {
        if (!blob) { reject(new Error('Could not encode the photo.')); return; }
        var base = (name || 'photo').replace(/\.(heic|heif)$/i, '') || 'photo';
        resolve(new File([blob], base + '.jpg', { type: 'image/jpeg' }));
      }, 'image/jpeg', 0.92);
    });
  }
  // Safari can decode HEIC itself: draw it via an <img> onto a canvas.
  function nativeHeicToJpeg(file) {
    return new Promise(function (resolve, reject) {
      var url = URL.createObjectURL(file);
      var img = new Image();
      img.onload = function () {
        try {
          if (!img.naturalWidth) throw new Error('empty');
          var cv = document.createElement('canvas');
          cv.width = img.naturalWidth; cv.height = img.naturalHeight;
          cv.getContext('2d').drawImage(img, 0, 0);
          try { URL.revokeObjectURL(url); } catch (e) {}
          resolve(canvasToJpegFile(cv, file.name));
        } catch (e) { try { URL.revokeObjectURL(url); } catch (e2) {} reject(e); }
      };
      img.onerror = function () { try { URL.revokeObjectURL(url); } catch (e) {} reject(new Error('native HEIC decode unsupported')); };
      img.src = url;
    });
  }
  // Everyone else: decode with libheif (handles grid-tiled iPhone HEICs).
  function libheifToJpeg(file) {
    return ensureHeicLib()
      .then(function () { return file.arrayBuffer(); })
      .then(function (buf) {
        var mod = window.libheif();
        var decoder = new mod.HeifDecoder();
        var images = decoder.decode(new Uint8Array(buf));
        if (!images || !images.length) throw new Error('No image in the HEIC file.');
        var image = images[0];
        var w = image.get_width(), h = image.get_height();
        var cv = document.createElement('canvas');
        cv.width = w; cv.height = h;
        var ctx = cv.getContext('2d');
        var imageData = ctx.createImageData(w, h);
        return new Promise(function (resolve, reject) {
          image.display(imageData, function (out) {
            if (!out) { reject(new Error('Could not decode the HEIC file.')); return; }
            ctx.putImageData(imageData, 0, 0);
            resolve(canvasToJpegFile(cv, file.name));
          });
        });
      });
  }
  // Resolve to { file, contentType } ready to upload and preview.
  function prepareUploadFile(file) {
    if (!isHeicFile(file)) {
      var ct = file.type || (/\.png$/i.test(file.name) ? 'image/png' : 'image/jpeg');
      return Promise.resolve({ file: file, contentType: ct });
    }
    setUploadMessage('Preparing your photo\u2026');
    return nativeHeicToJpeg(file)
      .catch(function () { return libheifToJpeg(file); })
      .then(function (jpg) { return { file: jpg, contentType: 'image/jpeg' }; })
      .catch(function (e) {
        // Conversion failed (very large image, old device, blocked CDN): upload
        // the original so the order can still complete; preview may be missing.
        console.warn('CYOC HEIC conversion failed', e);
        return { file: file, contentType: file.type || 'image/heic' };
      });
  }

  function handleFile(file) {
    resetUploadUi();
    if (!file) return;
    if (CFG.allowedTypes.indexOf(file.type) === -1 && !/\.(jpe?g|png|heic|heif)$/i.test(file.name)) {
      showUploadError('Please choose a JPG, PNG or HEIC image.');
      return;
    }
    if (file.size > CFG.maxUploadBytes) {
      showUploadError('That image is too large. The maximum size is 30 MB.');
      return;
    }

    // Show ONE progress indicator: the on-preview placeholder overlay when the
    // placeholder is on screen (preferred), otherwise the editor photo-panel status.
    if (el.placeholder && !el.placeholder.hidden) {
      showPlaceholderUploading(true);
    } else {
      el.uploadStatus.hidden = false;
    }
    setUploadMessage('Uploading your photo\u2026');
    startUploadProgress();
    var uploadStartedAt = Date.now();

    prepareUploadFile(file)
      .then(function (prepared) {
        var uploadFile = prepared.file;
        var contentType = prepared.contentType;
        setUploadMessage('Uploading your photo\u2026');
        return authorise(contentType, uploadFile.size)
          .then(function (auth) {
            return putToS3(auth, uploadFile, contentType).then(function () { return auth; });
          })
          .then(function (auth) {
            state.uploadedAssetKey = auth.key;
            // Keep the bar filling for a visible minimum, then complete to 100%
            // and HOLD it briefly so the done state is seen before swapping to
            // the photo controls.
            var wait = Math.max(0, 700 - (Date.now() - uploadStartedAt));
            setTimeout(function () {
              setUploadMessage('Photo uploaded.');
              finishUploadProgress();
              showPlaceholderUploading(false);
              setTimeout(function () {
                showPhotoControls(true);
                updateAddState();
                loadPreviewImage(uploadFile);
              }, 500);
            }, wait);
          });
      })
      .catch(function (err) {
        console.error('CYOC upload failed', err);
        showUploadError(err && err.message ? err.message : 'Upload failed. Please try again.');
      });
  }

  function authorise(contentType, fileSize) {
    return fetch(CFG.endpoints.authorise, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ contentType: contentType, fileSize: fileSize })
    }).then(function (res) {
      return res.json().then(function (data) {
        if (!res.ok || !data.ok) throw new Error(data.error || 'Could not start the upload.');
        return data;
      });
    });
  }

  function putToS3(auth, file, contentType) {
    return new Promise(function (resolve, reject) {
      var form = new FormData();
      Object.keys(auth.upload.fields).forEach(function (k) { form.append(k, auth.upload.fields[k]); });
      if (!auth.upload.fields['Content-Type']) form.append('Content-Type', contentType);
      form.append('file', file);

      var xhr = new XMLHttpRequest();
      xhr.open('POST', auth.upload.url, true);
      // Progress is shown as an indeterminate animated bar (set busy in
      // handleFile); byte-level progress on the S3 POST is unreliable, and
      // several phases have no progress signal at all.
      xhr.onload = function () {
        if (xhr.status >= 200 && xhr.status < 300) resolve();
        else reject(new Error('The photo could not be uploaded (' + xhr.status + '). Please try again.'));
      };
      xhr.onerror = function () { reject(new Error('Network error while uploading. Please try again.')); };
      xhr.send(form);
    });
  }

  function loadPreviewImage(file) {
    state.heicPreviewUnavailable = false;
    var url = URL.createObjectURL(file);
    var img = new Image();
    img.onload = function () {
      sourceImage = img;
      state.sourceWidth = img.naturalWidth;
      state.sourceHeight = img.naturalHeight;
      state.rotation = 0;
      state.zoom = 1;
      state.cropX = 0.5;
      state.cropY = 0.5;
      el.zoom.value = '1';
      rebuildWorkingImage();
      try { URL.revokeObjectURL(url); } catch (e) {}
      renderPreview();
    };
    img.onerror = function () {
      try { URL.revokeObjectURL(url); } catch (e) {}
      sourceImage = null;
      state.workingUrl = '';
      state.workingW = 0;
      state.workingH = 0;
      state.heicPreviewUnavailable = true;
      renderPreview();
    };
    img.src = url;
  }

  // ---- Position interactions ---------------------------------------------
  function panBy(dxDisplay, dyDisplay) {
    if (!state.workingW) return;
    var dims = renderDims();
    var borderPx = Math.round((CFG.borderFraction[state.borderWidth] || 0) * Math.min(dims.w, dims.h));
    var innerW = Math.max(1, dims.w - 2 * borderPx);
    var innerH = Math.max(1, dims.h - 2 * borderPx);
    var coverScale = Math.max(innerW / state.workingW, innerH / state.workingH);
    var dispW = state.workingW * coverScale * state.zoom;
    var dispH = state.workingH * coverScale * state.zoom;
    var s = fitScale(dims.w, dims.h);
    var rangeX = dispW - innerW;
    var rangeY = dispH - innerH;
    if (rangeX > 0) state.cropX = clamp(state.cropX - (dxDisplay / s) / rangeX, 0, 1);
    if (rangeY > 0) state.cropY = clamp(state.cropY - (dyDisplay / s) / rangeY, 0, 1);
    renderPreview();
  }

  function wirePositioning() {
    var pointers = {};
    var lastPinchDist = 0;
    var lastX = 0, lastY = 0;
    var dragging = false;

    el.frame.addEventListener('pointerdown', function (e) {
      // Only the front photo is draggable. On other faces (notably the inside
      // message editor) capturing the pointer here would stop the click from
      // focusing the contenteditable, so a text cursor never appears.
      if (state.face !== 'front') return;
      if (!state.workingW) return;
      el.frame.setPointerCapture(e.pointerId);
      pointers[e.pointerId] = { x: e.clientX, y: e.clientY };
      if (Object.keys(pointers).length === 1) { dragging = true; lastX = e.clientX; lastY = e.clientY; }
    });
    el.frame.addEventListener('pointermove', function (e) {
      if (!pointers[e.pointerId]) return;
      pointers[e.pointerId] = { x: e.clientX, y: e.clientY };
      var ids = Object.keys(pointers);
      if (ids.length >= 2) {
        var a = pointers[ids[0]], b = pointers[ids[1]];
        var dist = Math.hypot(a.x - b.x, a.y - b.y);
        if (lastPinchDist > 0) {
          state.zoom = clamp(state.zoom * (dist / lastPinchDist), 1, 8);
          el.zoom.value = String(state.zoom);
          renderPreview();
        }
        lastPinchDist = dist;
        dragging = false;
      } else if (dragging) {
        panBy(e.clientX - lastX, e.clientY - lastY);
        lastX = e.clientX; lastY = e.clientY;
      }
    });
    function up(e) {
      delete pointers[e.pointerId];
      if (Object.keys(pointers).length < 2) lastPinchDist = 0;
      if (Object.keys(pointers).length === 0) dragging = false;
    }
    el.frame.addEventListener('pointerup', up);
    el.frame.addEventListener('pointercancel', up);

    el.zoom.addEventListener('input', function () {
      state.zoom = clamp(parseFloat(el.zoom.value) || 1, 1, 8);
      renderPreview();
    });
    el.rotate.addEventListener('click', function () {
      if (!sourceImage) return;
      state.rotation = (state.rotation + 90) % 360;
      state.cropX = 0.5; state.cropY = 0.5;
      rebuildWorkingImage();
      renderPreview();
    });
    el.resetPos.addEventListener('click', function () {
      state.zoom = 1; state.cropX = 0.5; state.cropY = 0.5; state.rotation = 0;
      el.zoom.value = '1';
      if (sourceImage) rebuildWorkingImage();
      renderPreview();
    });
    el.replace.addEventListener('click', function () { el.file.click(); });
  }

  // ---- Phase + tab navigation --------------------------------------------
  function goPhase(name) {
    state.phase = name;
    el.phaseFormat.hidden = name !== 'format';
    el.phaseEditor.hidden = name !== 'editor';
    el.navFormat.style.display = name === 'format' ? 'flex' : 'none';
    el.navEditor.hidden = name !== 'editor';
    el.navEditor.style.display = name === 'editor' ? 'flex' : 'none';
    el.thumbs.hidden = name !== 'editor';
    if (name === 'editor') { updateEditorSummary(); updateAddState(); selectTab('photo'); }
    else { state.face = 'front'; }
    renderPreview();
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(updateScrollCue, 80);
  }

  function selectTab(tab) {
    state.tab = tab;
    Array.prototype.forEach.call(el.tabs.querySelectorAll('.cyoc__tab'), function (b) {
      var on = b.getAttribute('data-tab') === tab;
      b.classList.toggle('is-active', on);
      b.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    Array.prototype.forEach.call(el.phaseEditor.querySelectorAll('.cyoc__tabpanel'), function (pnl) {
      pnl.hidden = pnl.getAttribute('data-tab') !== tab;
    });
    // Selecting a tab shows the matching card face in the main preview. The
    // Inside tab keeps whichever inside page is already active (so switching
    // pages via the thumbnails works), defaulting to the inside-right page.
    if (tab === 'inside') {
      if (state.face !== 'inside-front' && state.face !== 'inside-back') state.face = 'inside-back';
    } else {
      state.face = 'front';
    }
    renderPreview();
    if (tab === 'inside') { revealPageSwitch(); focusInsideEditor(); }
  }

  // On a phone the tab bar sits below the card, so opening the Inside tab could
  // leave the page switcher and card scrolled out of view. Bring them back into
  // view (only if needed) so both inside pages are obviously reachable.
  function revealPageSwitch() {
    if (!el.pageSwitch || el.pageSwitch.hidden || window.innerWidth >= 860) return;
    var r = el.pageSwitch.getBoundingClientRect();
    if (r.top >= 60 && r.top <= window.innerHeight * 0.4) return;
    var top = (window.pageYOffset || document.documentElement.scrollTop || 0) + r.top - 80;
    try { window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' }); }
    catch (e) { window.scrollTo(0, Math.max(0, top)); }
  }

  // Drop the caret into the preview when the Inside tab opens so a cursor shows
  // and it's clearly editable, matching the personalisation page (which focuses
  // on load). setTimeout(0) lets the inside face render/become visible first so
  // the focus reliably takes.
  function focusInsideEditor() {
    setTimeout(function () {
      try {
        el.insideText.focus({ preventScroll: true });
        placeCaretAtEnd(el.insideText);
      } catch (e) {}
    }, 0);
  }

  function selectFace(face) {
    // Both inside pages live under the Inside tab (shared font/size controls).
    if (face === 'inside-front' || face === 'inside-back') {
      state.face = face;
      selectTab('inside');
      return;
    }
    if (face === 'front' && state.tab === 'inside') { selectTab('photo'); return; }
    state.face = face;
    renderPreview();
  }

  function syncOrientButtons() {
    [el.orientation, el.orientationEditor].forEach(function (group) {
      if (!group) return;
      Array.prototype.forEach.call(group.querySelectorAll('[data-orientation]'), function (b) {
        var on = b.getAttribute('data-orientation') === state.orientation;
        b.classList.toggle('is-selected', on);
        b.setAttribute('aria-checked', on ? 'true' : 'false');
      });
    });
  }
  function setOrientation(val) {
    if (val !== 'portrait' && val !== 'landscape') return;
    state.orientation = val;
    syncOrientButtons();
    renderPreview();
    measureInside();
    updateEditorSummary();
    updateAddState();
  }

  function updateEditorSummary() {
    var v = activeVariant();
    var sizeLabel = state.kind === 'pack' ? 'Pack of 10 (7×5")' : (state.size === 'a4' ? 'Jumbo (A4)' : 'Standard (7×5")');
    var orient = state.orientation === 'landscape' ? 'Landscape' : 'Portrait';
    el.editorSummary.textContent = sizeLabel + ' · ' + orient + ' · ';
    var strong = document.createElement('strong');
    strong.textContent = money(v.price) + (state.kind === 'pack' ? ' per pack' : (state.quantity > 1 ? ' each' : ''));
    el.editorSummary.appendChild(strong);
  }

  function updateAddState() {
    var v = activeVariant();
    var ready = !!state.uploadedAssetKey && v && v.id && !state.insideTooLong && !state.insideTooLongLeft;
    el.add.disabled = !ready;
    if (LIST_MODE) { el.add.textContent = 'Use this card in my list'; return; }
    el.add.textContent = ready ? ('Add to basket · ' + money(v.price * state.quantity)) : 'Add to basket';
  }

  // ---- Format wiring ------------------------------------------------------
  function applyKind() {
    var isPack = state.kind === 'pack';
    var title = el.sizeField.querySelector('.cyoc__field-title');
    if (isPack) {
      state.size = '7x5';
      Array.prototype.forEach.call(el.size.querySelectorAll('[data-size]'), function (b) {
        var on = b.getAttribute('data-size') === '7x5';
        b.classList.toggle('is-selected', on);
        b.setAttribute('aria-checked', on ? 'true' : 'false');
      });
      el.size.hidden = true;
      el.packInfo.hidden = false;
      if (title) title.textContent = 'Pack of 10';
      el.qtyLabel.textContent = 'Number of packs';
      el.insidePackNote.hidden = false;
    } else {
      el.size.hidden = false;
      el.packInfo.hidden = true;
      if (title) title.textContent = 'Pick a size';
      el.qtyLabel.textContent = 'Quantity';
      el.insidePackNote.hidden = true;
    }
    setQty(state.quantity);
    renderPreview();
  }

  function setQty(n) {
    var maxQ = state.kind === 'pack' ? (CFG.maxQtyPack || 50) : (CFG.maxQtySingle || 10);
    state.quantity = clamp(parseInt(n, 10) || 1, 1, maxQ);
    el.qty.value = String(state.quantity);
    if (el.qty) el.qty.max = String(maxQ);
    updateEditorSummary();
    updateAddState();
  }

  // ---- Inside message ------------------------------------------------------
  // ---- Persist + add to basket -------------------------------------------
  function buildRecipe() {
    var r = {
      uploadedAssetKey: state.uploadedAssetKey,
      cardSize: state.size,
      orientation: state.orientation,
      layout: state.borderWidth !== 'none' ? 'border' : 'full',
      isPack: state.kind === 'pack',
      quantity: state.quantity,
      cropX: state.cropX,
      cropY: state.cropY,
      zoom: state.zoom,
      rotation: state.rotation,
      borderWidth: state.borderWidth,
      sourceWidth: state.sourceWidth || undefined,
      sourceHeight: state.sourceHeight || undefined
    };
    if (state.borderWidth !== 'none') r.borderColour = state.borderColour;
    if (state.frontText.trim()) {
      r.frontText = state.frontText.trim();
      r.frontFont = state.frontFont;
      r.frontColour = state.frontColour;
      r.frontSize = state.frontSize;
      r.frontX = Math.round(state.frontX * 10000) / 10000;
      r.frontY = Math.round(state.frontY * 10000) / 10000;
    }
    if (!LIST_MODE && state.insideMessage.trim()) {
      r.insideMessage = state.insideMessage.trim();
      r.insideFont = state.insideFont;
      r.insideTextSize = state.insideSize;
    }
    return r;
  }

  function persistDesign() {
    return fetch(CFG.endpoints.persist, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(buildRecipe())
    }).then(function (res) {
      return res.json().then(function (data) {
        if (!res.ok || !data.ok) throw new Error(data.error || 'We could not save your design. Please try again.');
        return data;
      });
    });
  }

  function lineProperties() {
    var props = {};
    props['_gigglz_uid'] = makeUid();
    props['_gigglz_front_source'] = 'custom';
    props['_gigglz_custom_design_id'] = state.designFullId;
    props['_gigglz_orientation'] = state.orientation;
    props['_gigglz_back_asset_key'] = CFG.backAssetKey;
    var msgR = state.insideMessage.trim();
    var msgL = state.insideMessageLeft.trim();
    var hasR = msgR.length > 0;
    var hasL = msgL.length > 0;
    props['_gigglz_message_present'] = hasR ? 'true' : 'false';
    if (hasR) props['_gigglz_message'] = msgR;
    props['_gigglz_message_left_present'] = hasL ? 'true' : 'false';
    if (hasL) props['_gigglz_message_left'] = msgL;
    // Both inside pages share one font and size.
    if (hasR || hasL) {
      props['_gigglz_font'] = state.insideFont;
      props['_gigglz_text_size'] = state.insideSize;
    }
    if (state.kind === 'pack') {
      props['_gigglz_item_type'] = 'card_pack';
      props['_gigglz_rate_profile'] = '7x5_pack';
      props['_gigglz_prodigi_sku'] = CFG.packProdigiSku;
      props['_gigglz_pack_size'] = String(CFG.packSize || 10);
      props['_gigglz_pack_kind'] = 'gigglz_fixed';
      props['_gigglz_send_to'] = 'me';
    } else {
      props['_gigglz_item_type'] = 'card';
      props['_gigglz_rate_profile'] = state.size;
    }
    return props;
  }

  function showAddError(msg) {
    el.addError.textContent = msg;
    el.addError.hidden = false;
    try { el.addError.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) {}
  }

  function useInList() {
    var v = CFG.variants['7x5'];
    el.add.disabled = true;
    el.add.textContent = 'Saving your design…';
    persistDesign()
      .then(function (data) {
        state.designFullId = data.fullId;
        var payload = {
          designId: data.fullId,
          variantId: v.id,
          pricePence: Math.round(parseFloat(String(v.price).replace(/[^0-9.]/g, '')) * 100) || 0,
          orientation: state.orientation,
          backKey: CFG.backAssetKey,
          at: Date.now()
        };
        try { localStorage.setItem(SL_RETURN_KEY, JSON.stringify(payload)); } catch (e) {
          throw new Error('Your browser blocked saving the design for your list. Please allow site storage and try again.');
        }
        el.add.textContent = 'Added to your list';
        if (LIST_RETURN) {
          var token = '';
          try { token = btoa(unescape(encodeURIComponent(JSON.stringify(payload)))); } catch (e) {}
          window.location.href = LIST_RETURN + (token ? '?cyoc=' + encodeURIComponent(token) : '');
          return;
        }
        if (el.listBanner) el.listBanner.innerHTML = '<b>Done. Your card is in your list\'s tray.</b> You can close this tab and carry on with your list.';
        setTimeout(function () { try { window.close(); } catch (e) {} }, 900);
      })
      .catch(function (err) {
        console.error('CYOC list save failed', err);
        showAddError(err && err.message ? err.message : 'Something went wrong. Please try again.');
        updateAddState();
      });
  }

  function addToCart() {
    el.addError.hidden = true;
    var v = activeVariant();
    if (!v || !v.id) { showAddError('Sorry, this product is not available right now.'); return; }
    if (!state.uploadedAssetKey) { selectTab('photo'); showAddError('Please add a photo first.'); return; }
    if (state.insideTooLong || state.insideTooLongLeft) { selectFace(state.insideTooLongLeft ? 'inside-front' : 'inside-back'); showAddError('Your inside message is too long to fit on the card. Please shorten it or pick a smaller size.'); return; }
    if (dpiTooLow() && !window.confirm('This photo may print a little soft at this size. Add it anyway?')) return;
    if (LIST_MODE) { useInList(); return; }

    el.add.disabled = true;
    el.add.textContent = 'Saving your design…';

    persistDesign()
      .then(function (data) {
        state.designFullId = data.fullId;
        el.add.textContent = 'Adding to basket…';
        var body = { id: v.id, quantity: state.quantity, properties: lineProperties() };
        return fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body)
        });
      })
      .then(function (res) {
        return res.json().then(function (data) {
          if (!res.ok || data.status) throw new Error(data.description || data.message || 'Could not add to basket.');
          return data;
        });
      })
      .then(function () {
        window.location.href = '/cart';
      })
      .catch(function (err) {
        console.error('CYOC add to cart failed', err);
        showAddError(err && err.message ? err.message : 'Something went wrong. Please try again.');
        updateAddState();
      });
  }

  // ---- Init ---------------------------------------------------------------
  function initListMode() {
    if (!LIST_MODE) return;
    state.kind = 'single';
    state.size = '7x5';
    state.quantity = 1;
    if (el.root) el.root.classList.add('cyoc--list');
    var banner = document.createElement('div');
    banner.className = 'cyoc__list-banner';
    banner.setAttribute('role', 'note');
    banner.style.cssText = 'margin:0 0 14px;padding:12px 14px;border-radius:10px;background:#FFF0FA;border:1px solid #FF2EB7;font-size:14px;line-height:1.45';
    banner.innerHTML = '<b>Making a card for your list.</b> Design the front here. You\'ll write the inside message on your list, where we add each person\'s name.' +
      (LIST_RETURN ? ' <a href="' + LIST_RETURN + '" style="color:#FF2EB7;font-weight:700;white-space:nowrap">Back to your list without a card</a>' : '');
    // Inside the padded content column, not edge to edge.
    var host = (el.root && el.root.querySelector('.cyoc__inner')) || el.root || el.stage;
    if (host) host.insertBefore(banner, host.firstChild);
    el.listBanner = banner;
    // Only a single 7x5 card makes sense in a list.
    // The inside message is written on the list (with each person's name),
    // so the inside pages are not editable here.
    Array.prototype.forEach.call(document.querySelectorAll('#cyoc-thumbs [data-face="inside-front"], #cyoc-thumbs [data-face="inside-back"], .cyoc__tab[data-tab="inside"], #cyoc-pageswitch'), function (n) { n.hidden = true; n.style.display = 'none'; });
    state.insideMessage = '';
    state.insideMessageLeft = '';
    var kindField = el.kind && el.kind.closest ? el.kind.closest('.cyoc__field') : null;
    [kindField, el.sizeField, $('cyoc-qty-field')].forEach(function (node) { if (node) node.hidden = true; });
  }

  function init() {
    initListMode();
    // Format
    wireToggle(el.kind, 'data-kind', function (val) { state.kind = val; applyKind(); });
    wireToggle(el.size, 'data-size', function (val) { if (state.kind === 'pack') return; state.size = val; renderPreview(); });
    el.orientation.addEventListener('click', function (ev) { var b = ev.target.closest('[data-orientation]'); if (b) setOrientation(b.getAttribute('data-orientation')); });
    if (el.orientationEditor) el.orientationEditor.addEventListener('click', function (ev) { var b = ev.target.closest('[data-orientation]'); if (b) setOrientation(b.getAttribute('data-orientation')); });
    el.qtyMinus.addEventListener('click', function () { setQty(state.quantity - 1); });
    el.qtyPlus.addEventListener('click', function () { setQty(state.quantity + 1); });
    el.qty.addEventListener('change', function () { setQty(el.qty.value); });
    applyKind();

    // Upload
    el.file.addEventListener('change', function () { handleFile(el.file.files && el.file.files[0]); });
    ['dragover', 'dragenter'].forEach(function (t) {
      el.dropzone.addEventListener(t, function (e) { e.preventDefault(); el.dropzone.classList.add('is-dragover'); });
    });
    ['dragleave', 'drop'].forEach(function (t) {
      el.dropzone.addEventListener(t, function (e) { e.preventDefault(); el.dropzone.classList.remove('is-dragover'); });
    });
    el.dropzone.addEventListener('drop', function (e) {
      if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0]) handleFile(e.dataTransfer.files[0]);
    });

    // Position
    wirePositioning();

    // Click the empty preview card to add a photo (fast path, Moonpig-style).
    // Front face only: on the inside pages a tap means "type here", and the back
    // is locked, so neither should open the photo picker.
    el.frame.addEventListener('click', function () {
      if (state.uploadedAssetKey || state.face !== 'front') return;
      el.file.click();
    });

    // Border
    wireToggle(el.borderWidth, 'data-border', function (val) {
      state.borderWidth = val;
      el.borderColour.hidden = val === 'none';
      if (val !== 'none' && !el.borderColour.children.length) {
        buildSwatches(el.borderColour, state.borderColour, function (slug) { state.borderColour = slug; renderPreview(); });
      }
      renderPreview();
    });

    // Front text
    buildFontPicker(el.frontFont, CFG.frontFonts, state.frontFont, 'cyoc-front-font-label', function (slug) { state.frontFont = slug; renderPreview(); });
    buildSwatches(el.frontColour, state.frontColour, function (slug) { state.frontColour = slug; renderPreview(); });
    el.frontTextInput.addEventListener('input', function () {
      state.frontText = el.frontTextInput.value.slice(0, CFG.maxFrontText);
      el.frontTextCount.textContent = String(state.frontText.length);
      renderPreview();
    });
    el.frontSize.addEventListener('change', function () { state.frontSize = el.frontSize.value; renderPreview(); });
    // Front-text position: quick presets + free drag on the preview.
    if (el.frontPosition) {
      el.frontPosition.addEventListener('click', function (ev) {
        var b = ev.target.closest('[data-position]');
        if (b) applyFrontPreset(b.getAttribute('data-position'));
      });
    }
    syncFrontPresetButtons();
    wireFrontTextDrag();

    // Inside
    buildFontPicker(el.insideFont, CFG.insideFonts, state.insideFont, 'cyoc-inside-font-label', function (slug) { state.insideFont = slug; renderPreview(); measureInside(); updateAddState(); });
    fillSizeSelect(el.insideSize, CFG.insideSizes, state.insideSize);
    // The inside message is edited directly on the card preview.
    el.insideText.setAttribute('contenteditable', 'true');
    el.insideText.setAttribute('spellcheck', 'true');
    el.insideText.setAttribute('role', 'textbox');
    el.insideText.setAttribute('aria-multiline', 'true');
    el.insideText.setAttribute('aria-label', 'Inside message');
    el.insideText.setAttribute('data-placeholder', 'Enter your message here…');
    markInsideEmpty(state.insideMessage.trim().length === 0);
    el.insideText.addEventListener('input', onInsideEditorInput);
    el.insideText.addEventListener('keydown', insideEditorKeydown);
    el.insideText.addEventListener('paste', onInsideEditorPaste);
    el.insideSize.addEventListener('change', function () { state.insideSize = el.insideSize.value; renderPreview(); measureInside(); updateAddState(); });

    // Tabs, face thumbnails + phase nav
    el.tabs.addEventListener('click', function (ev) {
      var b = ev.target.closest('.cyoc__tab');
      if (b) selectTab(b.getAttribute('data-tab'));
    });
    el.thumbs.addEventListener('click', function (ev) {
      var b = ev.target.closest('.cyoc__thumb');
      if (b) selectFace(b.getAttribute('data-face'));
    });
    if (el.pageSwitch) el.pageSwitch.addEventListener('click', function (ev) {
      var b = ev.target.closest('.cyoc__ps-opt');
      if (b) selectFace(b.getAttribute('data-page'));
    });
    el.start.addEventListener('click', function () { refreshPreviewScaleH(); goPhase('editor'); });
    el.backBtn.addEventListener('click', function () { goPhase('format'); });
    el.add.addEventListener('click', addToCart);

    var _cyocLastW = window.innerWidth;
    window.addEventListener('resize', function () {
      // Ignore height-only changes (mobile address bar) so the preview stays put
      // while scrolling; only re-scale on an actual width/orientation change.
      if (window.innerWidth === _cyocLastW) return;
      _cyocLastW = window.innerWidth;
      refreshPreviewScaleH();
      renderPreview();
    });
    window.addEventListener('orientationchange', function () {
      _cyocLastW = window.innerWidth;
      refreshPreviewScaleH();
      setTimeout(renderPreview, 150);
      setTimeout(updateScrollCue, 200);
    });

    // Scroll cue wiring (mobile). Update on scroll (rAF-throttled), on any
    // configurator height change (ResizeObserver), and once on load. Tapping it
    // nudges the page down.
    if (el.scrollcue) {
      el.scrollcue.addEventListener('click', function () {
        window.scrollBy({ top: Math.max(180, window.innerHeight * 0.6), behavior: 'smooth' });
      });
      var _cueRaf = 0;
      window.addEventListener('scroll', function () {
        if (_cueRaf) return;
        _cueRaf = requestAnimationFrame(function () { _cueRaf = 0; updateScrollCue(); });
      }, { passive: true });
      if (window.ResizeObserver && el.layout) {
        try { new ResizeObserver(function () { updateScrollCue(); }).observe(el.layout); } catch (e) {}
      }
      setTimeout(updateScrollCue, 120);
    }

    window.addEventListener('beforeunload', function (e) {
      if (state.uploadedAssetKey && !state.designFullId) { e.preventDefault(); e.returnValue = ''; }
    });

    goPhase('format');
  }

  init();
})();
